import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { isNumber, isString } from 'lodash';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { getSingleLocationOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1 } from 'src/clients/locations/Device Locations API/types.gen';
import { DetailsSkeleton } from 'src/common/components/DetailsSkeleton';
import { SectionGridItem } from 'src/common/components/SectionGrid/SectionGridItem';
import { SectionGridRow } from 'src/common/components/SectionGrid/SectionGridRow';
import { SimData } from 'src/common/components/Sim/constants/sim';
import { rsimProductDetailsQueries } from 'src/rSimInventory/Details/utils/rsimProductDetailsQueries';
import { fetchRSim } from 'src/rSimInventory/RSimApi/rSimApi';
import { fetchSimInventoryEntry } from 'src/simInventory/SimInventoryApi/simInventoryApi';

type SimLocationDetailsInfoContentProps = {
  data: {
    apn: SimData['apn'];
    currentIpAddress: SimData['currentIpAddress'];
    ratType: SimData['ratType'];
    mobileNetworkOperator: SimData['mobileNetworkOperator'];
  };
  locationData?: CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1;
};

const SimLocationDetailsInfoContent: FC<SimLocationDetailsInfoContentProps> = (props) => {
  const { data, locationData } = props;

  const simLocationFeature = locationData?.features?.[0];
  const locationProperties = simLocationFeature?.properties;

  const { t } = useTranslation();

  const networkSectionTestId = 'locationDetails.fields.network';
  const sessionInformationSectionTestId = 'locationDetails.fields.session-information';
  const lastKnownLocationSectionTestId = 'locationDetails.fields.last-known-location';

  const { apn, currentIpAddress, ratType, mobileNetworkOperator } = data;

  return (
    <Fragment>
      <SectionGridRow
        data-testid={networkSectionTestId}
        title={t('simInventory.network')}
        sx={{ borderTop: 0 }}
      >
        <SectionGridItem
          label={t('simInventory.mobileNetworkOperator')}
          testId={`${networkSectionTestId}.field.mobile-network-operator`}
          value={mobileNetworkOperator}
          customGridSizes={{
            xsSize: 'auto' as const,
            smSize: 'auto' as const,
            mdSize: 'auto' as const,
            lgSize: 'auto' as const,
            xlSize: 'auto' as const,
          }}
          forceDisplay
        />
      </SectionGridRow>
      <SectionGridRow
        data-testid={sessionInformationSectionTestId}
        title={t('simInventory.sessionInformation')}
      >
        <SectionGridItem
          label={t('simInventory.apn')}
          testId={`${sessionInformationSectionTestId}.field.apn`}
          value={apn}
          forceDisplay
        />
        <SectionGridItem
          label={t('rSimInventory.lastIpAddress')}
          testId={`${sessionInformationSectionTestId}.field.last-ip-address`}
          value={currentIpAddress}
          forceDisplay
        />
        <SectionGridItem
          label={t('simInventory.lastRat')}
          testId={`${sessionInformationSectionTestId}.field.last-rat`}
          value={ratType}
          forceDisplay
        />
      </SectionGridRow>
      {locationData && (
        <SectionGridRow
          data-testid={lastKnownLocationSectionTestId}
          title={t('simInventory.lastKnownLocation')}
        >
          <SectionGridItem
            label={t('simInventory.latitude')}
            testId={`${lastKnownLocationSectionTestId}.field.latitude`}
            value={
              isNumber(simLocationFeature?.geometry?.coordinates?.[1])
                ? simLocationFeature.geometry.coordinates[1].toLocaleString()
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.longitude')}
            testId={`${lastKnownLocationSectionTestId}.field.longitude`}
            value={
              isNumber(simLocationFeature?.geometry?.coordinates?.[0])
                ? simLocationFeature.geometry.coordinates[0].toLocaleString()
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.lastUpdated')}
            testId={`${lastKnownLocationSectionTestId}.field.last-updated`}
            value={
              isString(locationProperties?.eventTimestamp)
                ? locationProperties.eventTimestamp
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.accuracy')}
            testId={`${lastKnownLocationSectionTestId}.field.accuracy`}
            value={
              isNumber(locationProperties?.accuracy)
                ? locationProperties.accuracy.toString()
                : undefined
            }
            forceDisplay
          />
        </SectionGridRow>
      )}
    </Fragment>
  );
};

type RSimLocationDetailsInfoProps = { euicc: string };

export const RSimLocationDetailsInfo: FC<RSimLocationDetailsInfoProps> = (props) => {
  const { euicc } = props;

  const { t } = useTranslation();

  const { data, status } = useQuery({
    queryKey: rsimProductDetailsQueries.location({ euicc }),
    queryFn: async ({ signal }) => {
      const rsimsDetails = await fetchRSim(euicc, signal);
      const rsimDetails = rsimsDetails.items[0];

      const profiles = await Promise.all(
        [rsimDetails.primaryIccid, rsimDetails.secondaryIccid].map((iccid) =>
          fetchSimInventoryEntry(iccid, undefined, signal),
        ),
      );

      return {
        rsimDetails,
        profiles,
      };
    },
    meta: {
      isLocationsDetails: true,
    },
  });

  const activeIccidData =
    data?.rsimDetails.lastActiveProfile === 1 || data?.rsimDetails.lastActiveProfile === 2
      ? data?.profiles[data.rsimDetails.lastActiveProfile - 1]
      : null;

  const simLocationDetailOptions = getSingleLocationOptions({
    query: {
      DeviceId: activeIccidData?.imsi ?? '',
    },
  });

  const hasImsi = !!activeIccidData?.imsi;

  const { data: locationData, status: locationStatus } = useQuery({
    ...simLocationDetailOptions,
    enabled: hasImsi,
  });

  if (status === 'pending' || (hasImsi && locationStatus === 'pending')) {
    return <DetailsSkeleton numberOfRepetitions={3} />;
  }

  if (status === 'error' || (hasImsi && locationStatus === 'error')) {
    return <Typography textAlign="center">{t('common.somethingWrong')}</Typography>;
  }

  const activeIccidIndex = data.rsimDetails.lastActiveProfile - 1;

  return (
    <Box data-testid="locationDetails.fields.rsim">
      <SimLocationDetailsInfoContent
        data={
          activeIccidIndex >= 0
            ? {
                apn: data.profiles[activeIccidIndex].apn,
                currentIpAddress: data.profiles[activeIccidIndex].currentIpAddress,
                ratType: data.profiles[activeIccidIndex].ratType,
                mobileNetworkOperator: data.profiles[activeIccidIndex].mobileNetworkOperator,
              }
            : {
                apn: undefined,
                currentIpAddress: undefined,
                ratType: undefined,
                mobileNetworkOperator: undefined,
              }
        }
        locationData={hasImsi ? locationData : undefined}
      />
    </Box>
  );
};
